/**
 * Public Landing Page
 * USLS-branded lead capture page matching ww2.uslawshield.com style.
 * Colors and dimensions extracted from ww2.uslawshield.com/firearm-safety-rules/
 * Route: /lp/:slug
 */

import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { supabase, anonSupabase } from "../services/supabase";
import { US_STATES } from "../types/event.types";

const anonClient = anonSupabase;

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onfocus",
      "onblur",
    ],
  });
}

interface LandingPageData {
  id: string;
  title: string;
  slug: string;
  body_html: string | null;
  image_url: string | null;
  image_position: "left" | "right";
  promo_code_enabled: boolean;
  is_published: boolean;
}

/*
 * Exact colors from ww2.uslawshield.com source:
 * Page background:     #f8efda  (--awb-custom16)
 * Banner red:          #cf212a  (inline --awb-bg-color)
 * Submit button:       #a51d31  (--awb-custom12)
 * Button hover:        #891022  (--button_gradient_top_color_hover)
 * Footer bg:           #25273f  (--awb-color6)
 * Dark navy text:      #0f155c  (--awb-color8)
 * Required fields:     #c02b0a  (inline)
 * Green accent:        #65bc7b  (--primary_color)
 * Light border:        #cdc9c1  (--awb-custom11)
 */

// Form inputs: white boxes matching USLS Gravity Forms style
const inputClass =
  "w-full px-4 py-3 bg-white border border-[#cdc9c1] rounded text-[15px] text-[#333] placeholder-gray-400 focus:outline-none focus:border-[#65bc7b] focus:ring-1 focus:ring-[#65bc7b] transition-colors";
const inputErrorClass =
  "w-full px-4 py-3 bg-white border border-red-400 rounded text-[15px] text-[#333] placeholder-gray-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors";

export default function LandingPagePublic() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";

  const [landingPage, setLandingPage] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    zipCode: "",
    state: "",
    promoCode: "",
    emailConsent: false,
    smsConsent: false,
  });
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load landing page by slug
  useEffect(() => {
    async function loadPage() {
      if (!slug) {
        setError("Page not found");
        setLoading(false);
        return;
      }
      try {
        const client = isPreview ? supabase : anonClient;
        let query = client.from("landing_pages").select("*").eq("slug", slug);

        if (!isPreview) {
          query = query.eq("is_published", true);
        }

        const { data, error: fetchError } = await query.maybeSingle();

        if (fetchError) {
          throw fetchError;
        }
        if (!data) {
          setError("Page not found");
          setLoading(false);
          return;
        }

        setLandingPage(data as LandingPageData);
      } catch (err) {
        console.error("Error loading landing page:", err);
        setError("Failed to load page");
      } finally {
        setLoading(false);
      }
    }
    loadPage();
  }, [slug, isPreview]);

  // Format phone as (XXX) XXX-XXXX
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) {
      return "";
    }
    if (digits.length <= 3) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Strip phone to digits only
  const phoneDigits = (value: string): string => value.replace(/\D/g, "");

  // Restrict zip to 5 digits
  const formatZip = (value: string): string =>
    value.replace(/\D/g, "").slice(0, 5);

  const handleFieldChange = (field: string, value: string | boolean) => {
    let formatted = value;
    if (field === "phone" && typeof value === "string") {
      formatted = formatPhone(value);
    } else if (field === "zipCode" && typeof value === "string") {
      formatted = formatZip(value);
    }
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.firstName.trim()) {
      errors.firstName = "Required";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Required";
    }
    if (!formData.email.trim()) {
      errors.email = "Required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Required";
    } else if (phoneDigits(formData.phone).length !== 10) {
      errors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!formData.zipCode.trim()) {
      errors.zipCode = "Required";
    } else if (!/^\d{5}$/.test(formData.zipCode)) {
      errors.zipCode = "Please enter a valid 5-digit zip code";
    }
    if (!formData.state) {
      errors.state = "Required";
    }
    if (!formData.emailConsent) {
      errors.emailConsent = "Email consent is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !landingPage) {
      return;
    }

    setSubmitting(true);
    setValidationErrors({});

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/landing-page-submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            landingPageId: landingPage.id,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim().toLowerCase(),
            phone: phoneDigits(formData.phone),
            zipCode: formData.zipCode.trim(),
            state: formData.state,
            promoCode: formData.promoCode.trim() || undefined,
            emailConsent: formData.emailConsent,
            smsConsent: formData.smsConsent,
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        setValidationErrors({
          _form: data.error || "Submission failed. Please try again.",
        });
        return;
      }

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Submit error:", err);
      setValidationErrors({ _form: "An error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8efda]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a51d31]" />
      </div>
    );
  }

  // Error
  if (error || !landingPage) {
    return (
      <div className="min-h-screen bg-[#f8efda] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <img
              src="/USLSlogonobackground.png"
              alt="U.S. LawShield"
              className="mx-auto mb-8"
              style={{ maxWidth: 180 }}
            />
            <h1 className="text-2xl font-bold text-[#0f155c] mb-2">
              Page Not Found
            </h1>
            <p className="text-[#333]">
              {error || "This page does not exist."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f8efda] flex flex-col">
        {isPreview && (
          <div className="bg-yellow-400 text-yellow-900 text-center py-2 text-sm font-medium">
            Preview Mode — This page is not published yet
          </div>
        )}
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center max-w-lg mx-auto px-4">
            <img
              src="/USLSlogonobackground.png"
              alt="U.S. LawShield"
              className="mx-auto mb-8"
              style={{ maxWidth: 180 }}
            />
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-[#65bc7b]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1
              className="text-3xl text-[#0f155c] mb-4"
              style={{ fontFamily: "'Georgia', serif", fontStyle: "italic" }}
            >
              Let's make safety and education a priority!
            </h1>
            <p className="text-lg text-[#333]">
              Thank you for your interest. We'll be in touch soon.
            </p>
          </div>
        </div>
        <LPFooter />
      </div>
    );
  }

  const imageOnLeft = landingPage.image_position === "left";

  return (
    <div className="min-h-screen bg-[#f8efda] flex flex-col">
      {isPreview && (
        <div className="bg-yellow-400 text-yellow-900 text-center py-2 text-sm font-medium">
          Preview Mode — This page is not published yet
        </div>
      )}

      {/* Shield logo - centered, no nav */}
      <div className="text-center py-5">
        <img
          src="/USLSlogonobackground.png"
          alt="U.S. LawShield"
          className="mx-auto"
          style={{ maxWidth: 180 }}
        />
      </div>

      {/* Red hero banner with title — 80% width, rounded, with shadow */}
      <div className="max-w-6xl mx-auto w-full px-4 mb-5">
        <div
          className="mx-auto rounded-xl"
          style={{
            backgroundColor: "#cf212a",
            padding: "6% 10%",
            maxWidth: "80%",
            boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.3)",
            borderRadius: 12,
          }}
        >
          <h1
            className="text-white text-center leading-tight"
            style={{
              fontFamily:
                "'Graphik Regular', 'Inter', -apple-system, sans-serif",
              fontSize: "clamp(28px, 4vw, 58px)",
            }}
          >
            {landingPage.title}
          </h1>
        </div>
      </div>

      <main className="flex-1">
        {/* Two-column: image (40%) + body text (60%) */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
            {landingPage.image_url && (
              <div
                className={`md:col-span-2 ${imageOnLeft ? "md:order-1" : "md:order-2"}`}
              >
                <img
                  src={landingPage.image_url}
                  alt={landingPage.title}
                  className="w-full h-auto"
                  style={{ maxWidth: 400 }}
                />
              </div>
            )}

            {landingPage.body_html && (
              <div
                className={`${landingPage.image_url ? "md:col-span-3" : "md:col-span-5"} ${imageOnLeft ? "md:order-2" : "md:order-1"}`}
              >
                <div
                  className="prose max-w-none [&_p]:mb-4 [&_p]:leading-relaxed [&_strong]:text-[#1f2022]"
                  style={{
                    fontFamily:
                      "'Graphik Regular', 'Inter', -apple-system, sans-serif",
                    fontSize: 23,
                    color: "#333",
                    lineHeight: 1.6,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(landingPage.body_html),
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Sign Up section heading */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 mb-6">
          <h2
            className="text-center"
            style={{
              fontFamily:
                "'Graphik Regular', 'Inter', -apple-system, sans-serif",
              fontStyle: "italic",
              fontSize: "clamp(24px, 3vw, 36px)",
              color: "#25273f",
            }}
          >
            Sign Up Below!
          </h2>
        </div>

        {/* Lead capture form */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 pb-12">
          <form onSubmit={handleSubmit}>
            {validationErrors._form && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {validationErrors._form}
              </div>
            )}

            {/* Row 1: First Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
              <div>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleFieldChange("firstName", e.target.value)
                  }
                  placeholder="First Name*"
                  className={
                    validationErrors.firstName ? inputErrorClass : inputClass
                  }
                />
                {validationErrors.firstName && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleFieldChange("lastName", e.target.value)
                  }
                  placeholder="Last Name*"
                  className={
                    validationErrors.lastName ? inputErrorClass : inputClass
                  }
                />
                {validationErrors.lastName && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: Email, Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
              <div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  placeholder="Email*"
                  className={
                    validationErrors.email ? inputErrorClass : inputClass
                  }
                />
                {validationErrors.email && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.email}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange("phone", e.target.value)}
                  placeholder="Phone* (XXX) XXX-XXXX"
                  maxLength={14}
                  className={
                    validationErrors.phone ? inputErrorClass : inputClass
                  }
                />
                {validationErrors.phone && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Row 3: Zip Code, State, Promo Code */}
            <div
              className={`grid grid-cols-1 gap-x-6 gap-y-4 mb-8 ${
                landingPage.promo_code_enabled
                  ? "md:grid-cols-3"
                  : "md:grid-cols-2"
              }`}
            >
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.zipCode}
                  onChange={(e) => handleFieldChange("zipCode", e.target.value)}
                  placeholder="Zip Code*"
                  maxLength={5}
                  className={
                    validationErrors.zipCode ? inputErrorClass : inputClass
                  }
                />
                {validationErrors.zipCode && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.zipCode}
                  </p>
                )}
              </div>
              <div>
                <select
                  value={formData.state}
                  onChange={(e) => handleFieldChange("state", e.target.value)}
                  className={`${
                    validationErrors.state ? inputErrorClass : inputClass
                  } ${!formData.state ? "text-gray-400" : "text-[#333]"}`}
                >
                  <option value="">State of Residence*</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {validationErrors.state && (
                  <p className="text-xs text-red-500 mt-1">
                    {validationErrors.state}
                  </p>
                )}
              </div>
              {landingPage.promo_code_enabled && (
                <div>
                  <input
                    type="text"
                    value={formData.promoCode}
                    onChange={(e) =>
                      handleFieldChange("promoCode", e.target.value)
                    }
                    placeholder="Promo Code (if none enter NA)*"
                    className={inputClass}
                  />
                </div>
              )}
            </div>

            {/* Email consent (required) */}
            <div className="mb-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.emailConsent}
                  onChange={(e) =>
                    handleFieldChange("emailConsent", e.target.checked)
                  }
                  className="mt-0.5 h-4 w-4 rounded border-[#cdc9c1] text-[#65bc7b] focus:ring-[#65bc7b]"
                />
                <span className="text-sm text-[#333] leading-relaxed">
                  By checking this box, I consent to receive communications from
                  U.S. LawShield&reg; by email.*
                </span>
              </label>
              {validationErrors.emailConsent && (
                <p className="text-xs text-red-500 mt-1 ml-7">
                  {validationErrors.emailConsent}
                </p>
              )}
            </div>

            {/* SMS consent (optional) */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.smsConsent}
                  onChange={(e) =>
                    handleFieldChange("smsConsent", e.target.checked)
                  }
                  className="mt-0.5 h-4 w-4 rounded border-[#cdc9c1] text-[#65bc7b] focus:ring-[#65bc7b]"
                />
                <span className="text-sm text-[#333] leading-relaxed">
                  By checking this box, I consent to receive
                  promotional/marketing communications from U.S. LawShield&reg;
                  by mobile message (including but not limited to SMS and text
                  messages and/or via any automated dialing or texting system)
                  and/or telephone (including but not limited to via any
                  automatic telephone dialing system and/or artificial or
                  prerecorded voice). I understand my consent is not a condition
                  of purchase or participation, and I may revoke my consent at
                  any time. Message and data rates may apply.{" "}
                  <a
                    href="https://www.uslawshield.com/terms-and-conditions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#0f155c] hover:text-[#a51d31]"
                  >
                    Terms and Privacy
                  </a>
                  .
                </span>
              </label>
            </div>

            {/* Required fields note */}
            <div className="text-right mb-6">
              <span className="text-sm italic" style={{ color: "#c02b0a" }}>
                *Denotes Required Fields
              </span>
            </div>

            {/* Submit button — maroon (#a51d31), pill shape, with arrow */}
            <div className="text-center">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-12 py-3.5 text-white font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[15px] tracking-wide uppercase"
                style={{
                  backgroundColor: submitting ? "#891022" : "#a51d31",
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    (e.target as HTMLButtonElement).style.backgroundColor =
                      "#891022";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    (e.target as HTMLButtonElement).style.backgroundColor =
                      "#a51d31";
                  }
                }}
              >
                {submitting ? "Submitting..." : "Submit"}
                {!submitting && (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      <LPFooter />
    </div>
  );
}

/**
 * Footer matching ww2.uslawshield.com landing pages.
 * Background: #25273f (--awb-color6), text: #f8efda (--awb-custom16), 12px
 */
function LPFooter() {
  return (
    <footer style={{ backgroundColor: "#25273f" }}>
      <div
        className="max-w-6xl mx-auto px-4"
        style={{
          paddingTop: 40,
          paddingBottom: 30,
          color: "#f8efda",
          fontSize: 12,
          lineHeight: 1.7,
        }}
      >
        <p className="mb-4">
          The information provided herein is solely an overview intended to
          provide general information and should not be considered legal advice.
          All rights to this work are reserved. No part of this work may be
          reproduced or copied in any form or by any means without prior written
          consent.
        </p>
        <p>
          U.S. LawShield&reg; and affiliated entities are not a law firm.
          Membership or participation in any U.S. LawShield program, including
          the Legal Defense for Self Defense&trade; Program,
          EmergencyShield&trade; Program, and/or FFL DealerShield&trade;
          Program, is not a guarantee of any particular outcome. All legal
          services are provided by independent third-party program attorneys who
          are part of the program(s). Visit{" "}
          <a
            href="https://www.uslawshield.com"
            className="underline"
            style={{ color: "#f8efda" }}
            onMouseEnter={(e) =>
              ((e.target as HTMLAnchorElement).style.color = "#fff")
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLAnchorElement).style.color = "#f8efda")
            }
          >
            uslawshield.com
          </a>{" "}
          for terms, conditions, restrictions, and insurance licensing
          information.
        </p>
      </div>
    </footer>
  );
}
